import { DeviceOrientationControls } from './DeviceOrientationControls'

import * as THREE from 'three'

import { MathUtils, Vector3 } from 'three'

function sign (x : number) : number { return (x > 0) ? 1 : -1 }

function Vector3ToString (_vector:THREE.Vector3) {
  return `{${_vector.x}, ${_vector.y} ${_vector.z}}`
}

function EulerToString (_vector:THREE.Euler) {
  return `{${_vector.x}, ${_vector.y} ${_vector.z}}`
}

Vector3ToString(new THREE.Vector3(0, 0, 0))
EulerToString(new THREE.Euler(0, 0, 0))

function Wrap (
  x : number,
  a : number,
  b : number) : number {
  const range = b - a
  return x - (Math.floor(x / range) * range)
}

function ClampVector (
  targetVec : THREE.Vector3,
  minVec : THREE.Vector3,
  maxVec : THREE.Vector3) {
  targetVec.set(
    THREE.MathUtils.clamp(targetVec.x, minVec.x, maxVec.x),
    THREE.MathUtils.clamp(targetVec.y, minVec.y, maxVec.y),
    THREE.MathUtils.clamp(targetVec.z, minVec.z, maxVec.z)
  )
}
function WrapLerp (
  start : number,
  end : number,
  a : number,
  b : number,
  amount : number
) : number {
  const bActual =
      (b > end)
        ? start + (b - end)
        : (b < start)
            ? end - (start - b)
            : b

  const range = Math.abs(end - start)
  const halfRange = range / 2
  const normDiff = b - a

  const inverseDiff =
      (Math.min(a, bActual) - start) +
      (end - Math.max(a, bActual))

  if (b < end && b > start && halfRange > Math.abs(normDiff)) {
    return a + (normDiff * amount)
  } else {
    if (a < start + halfRange) {
      const newAmount = a - inverseDiff * amount
      return newAmount - (Math.floor((newAmount - start) / range) * range)
    } else { // we go the other way
      const newAmount = a + inverseDiff * amount
      return newAmount - (Math.floor((newAmount - start) / range) * range)
    }
  }
}

interface CameraDeviceOrientationParams {

    // orientation in real life to be used
    worldRange?: THREE.Vector2

    // degress in world space that the above is mapped to
    virtualRange?: THREE.Vector2

    hardVirtualRange?: THREE.Vector2,

    yRotationTarget: THREE.Object3D,
    xRotationTarget: THREE.Object3D
}

export default class CameraDeviceOrientationBehaviour {
  controls: DeviceOrientationControls | null = null

  dummyObject: THREE.Object3D = new THREE.Object3D()
  initialDummyRotation : THREE.Euler | null = null

  private get CurrentDeviceOirentation () : THREE.Euler {
    if (this.initialDummyRotation === null &&
            (this.dummyObject.rotation.x * THREE.MathUtils.RAD2DEG === -90 &&
             this.dummyObject.rotation.y * THREE.MathUtils.RAD2DEG === 0 &&
             this.dummyObject.rotation.z * THREE.MathUtils.RAD2DEG === 0) === false) {
      this.initialDummyRotation = this.dummyObject.rotation.clone()
    }

    const eul1 = this.dummyObject.rotation
    const eul2 = (this.initialDummyRotation != null) ? this.staticRotationOffset : this.uninitializedRotationOffset
    const eul3 =
        (this.initialDummyRotation != null)
          ? new THREE.Euler(
            (this.rotationAxisOffsetEnabled[0]) ? -this.initialDummyRotation.x : 0,
            (this.rotationAxisOffsetEnabled[1]) ? -this.initialDummyRotation.y : 0,
            (this.rotationAxisOffsetEnabled[2]) ? -this.initialDummyRotation.z : 0)
          : new THREE.Euler(0, 0, 0)
    const summedEular = new THREE.Euler()
    summedEular.x = eul1.x + eul2.x + eul3.x
    summedEular.y = eul1.y + eul2.y + eul3.y
    summedEular.z = eul1.z + eul2.z + eul3.z

    return summedEular
  }

  prevDeviceRotation = new THREE.Vector3(0, 0, 0)
  rotationCycleCount = new THREE.Vector3(0, 0, 0)

  protected readonly yRotateAxis: THREE.Object3D
  protected readonly xRotateAxis: THREE.Object3D

  worldRange: THREE.Vector2 = new THREE.Vector2(
    THREE.MathUtils.degToRad(90),
    THREE.MathUtils.degToRad(45)
  )

  private virtualRange = new THREE.Vector2(
    THREE.MathUtils.degToRad(25),
    THREE.MathUtils.degToRad(45)
  )

  private hardVirtualRange : THREE.Vector2 = new THREE.Vector2(
    THREE.MathUtils.DEG2RAD * 20,
    THREE.MathUtils.DEG2RAD * 999999
  )

  // private initialForwardRotation: THREE.Vector3
  private staticRotationOffset: THREE.Euler = new THREE.Euler(
    THREE.MathUtils.degToRad(0),
    THREE.MathUtils.degToRad(180),
    0)

  private uninitializedRotationOffset: THREE.Euler = new THREE.Euler(
    THREE.MathUtils.degToRad(90),
    THREE.MathUtils.degToRad(180),
    0)

  private rotationAxisOffsetEnabled : boolean[] = [false, true, false]

  private currentRotationOffset: THREE.Euler = new THREE.Euler()

  outOfRangeElapsed = 0
  outOfRangeLimit = 3

  constructor (
    params: CameraDeviceOrientationParams) {
    if (params.virtualRange != null) this.virtualRange = params.virtualRange
    if (params.worldRange != null) this.worldRange = params.worldRange
    if (params.hardVirtualRange != null) this.hardVirtualRange = params.hardVirtualRange

    this.dummyObject.name = 'dummyRotation'
    this.xRotateAxis = params.xRotationTarget
    this.yRotateAxis = params.yRotationTarget
  }

  OnEnable (): void {
    this.AssignStartingCameraPosition()

    this.controls = new DeviceOrientationControls(this.dummyObject)

    this.yRotateAxis.position.set(0, 0, 0)
    this.yRotateAxis.rotation.set(0, 0, 0)
    this.xRotateAxis.position.set(0, 0, 0)
    this.xRotateAxis.rotation.set(0, 0, 0)
  }

  private AssignStartingCameraPosition (): void {
    this.currentRotationOffset = new THREE.Euler(0, 0, 0)
  }

  Dispose () : void {}

  OnUpdate (_deltaTime : number): void {
    this.UpdateCamRotate(_deltaTime)
  }

  private UpdateCamRotate (_deltaTime : number): void {
    const unprocessedDeviceRotation = this.CurrentDeviceOirentation

    // before we do any processing, this maps device orientation to normal degrees to make it easier for use to do processing
    const processDeviceOirentation = new THREE.Euler(
      unprocessedDeviceRotation.x,
      unprocessedDeviceRotation.y + THREE.MathUtils.degToRad(180),
      unprocessedDeviceRotation.z
    )

    const affectiveDeviceRotation = processDeviceOirentation

    // this ensures the rotation cycles add up, instead of fliping around to the other end of the rotation range (if moev to 361 degrees for example)
    if (Math.abs(affectiveDeviceRotation.y - this.prevDeviceRotation.y) > THREE.MathUtils.degToRad(180)) {
      this.rotationCycleCount.y -= sign(affectiveDeviceRotation.y - this.prevDeviceRotation.y)
    }
    if (Math.abs(affectiveDeviceRotation.x - this.prevDeviceRotation.x) > THREE.MathUtils.degToRad(180)) {
      this.rotationCycleCount.x -= sign(affectiveDeviceRotation.x - this.prevDeviceRotation.y)
    }
    if (Math.abs(affectiveDeviceRotation.z - this.prevDeviceRotation.z) > THREE.MathUtils.degToRad(180)) {
      this.rotationCycleCount.x -= sign(affectiveDeviceRotation.z - this.prevDeviceRotation.z)
    }

    this.prevDeviceRotation.set(
      affectiveDeviceRotation.x,
      affectiveDeviceRotation.y,
      affectiveDeviceRotation.z)

    affectiveDeviceRotation.set(
      affectiveDeviceRotation.x + this.rotationCycleCount.x * THREE.MathUtils.degToRad(360),
      affectiveDeviceRotation.y + this.rotationCycleCount.y * THREE.MathUtils.degToRad(360),
      affectiveDeviceRotation.z + this.rotationCycleCount.z * THREE.MathUtils.degToRad(360)
    )

    // this allows use to customize the sensitivities of gyro data, so we can make different axis more/less sensitive
    const mapedOirentation = new Vector3(
      (affectiveDeviceRotation.x / this.worldRange.x) * this.virtualRange.x,
      (affectiveDeviceRotation.y / this.worldRange.y) * this.virtualRange.y,
      affectiveDeviceRotation.z
    )

    const projectedDir = new THREE.Vector3(
      mapedOirentation.x + this.currentRotationOffset.x,
      mapedOirentation.y + this.currentRotationOffset.y,
      mapedOirentation.z + this.currentRotationOffset.z
    )

    // here we impose a clamp if we want once
    const xBeyondLimit = (Math.abs(projectedDir.x) > this.hardVirtualRange.x / 2)
    const yBeyondLimit = (Math.abs(projectedDir.y) > this.hardVirtualRange.y / 2)
    const isBeyongLimits = xBeyondLimit || yBeyondLimit

    this.outOfRangeElapsed += ((isBeyongLimits) ? 1 : -2.5) * _deltaTime
    this.outOfRangeElapsed = Math.max(this.outOfRangeElapsed, 0)
    this.outOfRangeElapsed = Math.min(this.outOfRangeElapsed, 4)

    /*
    the following code needs to be maintained so when we decide to turn recentering back on, we have this for reference
    if (this.outOfRangeElapsed > this.outOfRangeLimit) {
      // this.currentRotationOffset.y -= ( yBeyondLimit ) ? Helper.sign(newDir.y) * 5 * App.DeltaTime : 0

      if (false) {
        this.currentRotationOffset.y = THREE.MathUtils.lerp(
          this.currentRotationOffset.y,
          -mapedOirentation.y,
          0.1)

        this.currentRotationOffset.x = THREE.MathUtils.lerp(
          this.currentRotationOffset.x,
          -mapedOirentation.x,
          0.1)
      }
    }
    */

    projectedDir.add(new Vector3(MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180))
    projectedDir.set(
      Wrap(projectedDir.x, THREE.MathUtils.degToRad(0), THREE.MathUtils.degToRad(360)),
      Wrap(projectedDir.y, THREE.MathUtils.degToRad(0), THREE.MathUtils.degToRad(360)),
      Wrap(projectedDir.z, THREE.MathUtils.degToRad(0), THREE.MathUtils.degToRad(360))
    )

    ClampVector(projectedDir,
      new THREE.Vector3(MathUtils.DEG2RAD * 180 - this.hardVirtualRange.x / 2, MathUtils.DEG2RAD * 180 - this.hardVirtualRange.y / 2, THREE.MathUtils.DEG2RAD * 360),
      new THREE.Vector3(MathUtils.DEG2RAD * 180 + this.hardVirtualRange.x / 2, MathUtils.DEG2RAD * 180 + this.hardVirtualRange.y / 2, THREE.MathUtils.DEG2RAD * 360)
    )

    projectedDir.sub(new Vector3(MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180))

    this.yRotateAxis.rotation.y = WrapLerp( // TODO alot of static numbers here
      THREE.MathUtils.degToRad(-180),
      THREE.MathUtils.degToRad(180),
      this.yRotateAxis.rotation.y,
      projectedDir.y,
      0.1)
    this.xRotateAxis.rotation.x = WrapLerp(
      THREE.MathUtils.degToRad(-180),
      THREE.MathUtils.degToRad(180),
      this.xRotateAxis.rotation.x,
      projectedDir.x,
      0.1)
  }
}

// for debugging reasons these three functions needs to stay commented out but maintained
// function printParnets (_object:THREE.Object3D | null) {
//   if (_object === null) return
//   console.log(_object.name)
//   printParnets(_object.parent)
// }

// function printParentRotation (_object:THREE.Object3D | null) {
//   if (_object === null) return
//   console.log(`${_object.name} {${_object.rotation.x}, ${_object.rotation.y}, ${_object.rotation.z}}`)
//   printParentRotation(_object.parent)
// }

// function PrintVector2 (_vector:THREE.Vector2) {
//   return `{${_vector.x}, ${_vector.y}}`
// }

/*
    A previous implementation of this component required the values to mapped to different values.
    However that seems to have changed. If it ever changes, we may need this code again. So please don't delete this commented section

    const processDeviceOirentation = new THREE.Euler(
      THREE.MathUtils.mapLinear(unprocessedDeviceRotation.x,
        THREE.MathUtils.degToRad(-180), // TODO : make all of these constants to reduce cpu overhead
        THREE.MathUtils.degToRad(180),
        THREE.MathUtils.degToRad(-180),
        THREE.MathUtils.degToRad(180)),
      THREE.MathUtils.mapLinear(unprocessedDeviceRotation.y,
        THREE.MathUtils.degToRad(-180),
        THREE.MathUtils.degToRad(180),
        THREE.MathUtils.degToRad(0 - 180),
        THREE.MathUtils.degToRad(360 + 180 - 180)),
      unprocessedDeviceRotation.z
    )
    */
